// Blog post date calculation utilities
// Posts are published every 6 days, starting from a reference date

const REFERENCE_DATE = new Date('2025-07-28'); // First post date
const POSTING_INTERVAL_DAYS = 6;

export interface BlogPost {
  id: string;
  title: string;
  date: Date;
  excerpt: string;
  filename: string;
}

export const blogPosts: BlogPost[] = [
  {
    id: 'spirit-of-tiki',
    title: 'The Spirit of Tiki: Origins and Cultural Significance',
    date: new Date('2025-07-28'),
    excerpt: 'Explore the deep cultural roots of tiki culture and its significance in Polynesian traditions.',
    filename: '2025-07-28-spirit-of-tiki.md'
  },
  {
    id: 'birth-of-cultural-revolution',
    title: 'The Birth of a Cultural Revolution: Tiki in Post-War America',
    date: new Date('2025-08-03'),
    excerpt: 'How tiki culture emerged in post-war America and captured the imagination of a generation.',
    filename: '2025-08-03-birth-of-cultural-revolution.md'
  },
  {
    id: 'american-tiki-renaissance',
    title: 'The American Tiki Renaissance: From Hollywood to Main Street',
    date: new Date('2025-08-09'),
    excerpt: 'How tiki culture crossed the Pacific and took root in American soil, creating a phenomenon that would define an era.',
    filename: '2025-08-09-american-tiki-renaissance.md'
  },
  {
    id: 'rum-revolution',
    title: 'The Rum Revolution: From Colonial Trade to Craft Distilling',
    date: new Date('2025-08-15'),
    excerpt: 'Discover how rum evolved from a colonial commodity to a sophisticated craft spirit with deep cultural roots.',
    filename: '2025-08-15-rum-revolution.md'
  },
  {
    id: 'tiki-bars-worldwide',
    title: 'Tiki Bars Around the World: From Trader Vic\'s to Modern Revival',
    date: new Date('2025-08-21'),
    excerpt: 'A global tour of the most iconic tiki bars and their influence on cocktail culture worldwide.',
    filename: '2025-08-21-tiki-bars-worldwide.md'
  },
  {
    id: 'rum-cocktail-mastery',
    title: 'Mastering the Art of Rum Cocktails: Techniques and Traditions',
    date: new Date('2025-08-27'),
    excerpt: 'Learn the essential techniques and traditions that make rum cocktails truly exceptional.',
    filename: '2025-08-27-rum-cocktail-mastery.md'
  },
  {
    id: 'distillery-spotlight',
    title: 'Distillery Spotlight: The World\'s Most Influential Rum Producers',
    date: new Date('2025-09-02'),
    excerpt: 'Meet the legendary distilleries that have shaped the rum industry and continue to innovate today.',
    filename: '2025-09-02-distillery-spotlight.md'
  },
  {
    id: 'tiki-culture-revival',
    title: 'The Modern Tiki Revival: Why Tropical Cocktails Are Back',
    date: new Date('2025-09-08'),
    excerpt: 'Exploring the resurgence of tiki culture and why tropical cocktails are experiencing a renaissance.',
    filename: '2025-09-08-tiki-culture-revival.md'
  }
];

export function getLatestBlogPost(): BlogPost | null {
  const now = new Date();
  
  // Find the most recent post that's not in the future
  const pastPosts = blogPosts.filter(post => post.date <= now);
  
  if (pastPosts.length === 0) {
    return null;
  }
  
  // Return the most recent past post
  return pastPosts.reduce((latest, current) => 
    current.date > latest.date ? current : latest
  );
}

export function getNextBlogPostDate(): Date | null {
  const latestPost = getLatestBlogPost();
  
  if (!latestPost) {
    return REFERENCE_DATE;
  }
  
  const nextDate = new Date(latestPost.date);
  nextDate.setDate(nextDate.getDate() + POSTING_INTERVAL_DAYS);
  
  return nextDate;
}

export function formatBlogDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

export function getDaysUntilNextPost(): number {
  const nextPostDate = getNextBlogPostDate();
  
  if (!nextPostDate) {
    return 0;
  }
  
  const now = new Date();
  const timeDiff = nextPostDate.getTime() - now.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
  
  return Math.max(0, daysDiff);
}
